import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { api } from 'api';

import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';

import { Signin } from './sign-in';
import { SigninSMS } from './SigninSMS';
import { SigninTwoFactor } from './SigninTwoFactor';

function SigninPage({ setTwoFactor }) {
  const { t } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { showModal } = useModal();

  const [state, setState] = useState({
    email: '',
    password: '',
    uuid: '',
    code: '',
    needsTwoFactor: false,
    needsSMS: false,
    sendingSMS: false,
    errors: {},
    error: '',
    disable: false,
  });
  const locationState = location.state;
  const client = useQueryClient();

  function handleChange(name, value) {
    setState((old) => ({ ...old, [name]: value, errors: {}, error: '' }));
  }

  async function signIn() {
    setState((old) => ({ ...old, error: '', disable: true }));
    const { email, password } = state;

    const res = await api.users.signIn(email, password);
    if (res.error) {
      if (res.data.nonFieldErrors.includes('company_needs_sso')) {
        showModal('SSO_REQUIRED', { email: state.email });
        setState((old) => ({ ...old, disable: false }));
      } else {
        setState((old) => ({
          ...old,
          errors: {
            nonFieldErrors: res.data.nonFieldErrors?.join(' ') || undefined,
            password: res.data.password?.join(' ') || undefined,
            email: res.data.email?.join(' ') || undefined,
          },
          disable: false,
        }));
      }
    } else {
      if (res.data.needs2FA) {
        setTwoFactor(res.data.needs2FA);
        setState((old) => ({ ...old, disable: false, needsTwoFactor: true, uuid: res.data.uuid }));
      } else {
        await client.resetQueries();
        const redir = locationState && (locationState.from ? locationState.from : '/');
        redir && navigate(redir);
      }
    }
  }

  async function signInTwoFactor() {
    setState((old) => ({ ...old, error: '', errors: {}, disable: true }));
    const { uuid, code } = state;

    const res = await api.users.signIn2fa(uuid, code);
    if (res.error) {
      setState((old) => ({ ...old, error: res.data.code, disable: false }));
    } else {
      client.resetQueries();
      const redir = locationState && (locationState.from ? locationState.from : '/');
      redir && navigate(redir);
    }
  }

  async function signInSMS() {
    setState((old) => ({ ...old, error: '', disable: true }));
    const { uuid, code } = state;

    const res = await api.users.signInSMS(uuid, code);
    if (res.error) {
      setState((old) => ({ ...old, error: res.data.code, disable: false }));
    } else {
      client.resetQueries();
      const redir = locationState && (locationState.from ? locationState.from : '/');
      redir && navigate(redir);
    }
  }

  async function useSms() {
    setState((old) => ({ ...old, error: '', sendingSMS: true }));
    const { uuid, code } = state;

    const res = await api.users.signInSendBackupSMS(uuid, code);
    if (res.error) {
      setState((old) => ({ ...old, sendingSMS: false, error: res.data.code, disable: false }));
    } else {
      setState((old) => ({ ...old, needsSMS: true, needsTwoFactor: false, disable: false }));
    }
  }

  if (state.needsSMS) {
    return (
      <SigninSMS
        t={t}
        code={state.code}
        onChange={handleChange}
        onSubmit={signInSMS}
        error={state.error}
        disableSubmit={state.disable}
      />
    );
  }

  if (state.needsTwoFactor) {
    return (
      <SigninTwoFactor
        t={t}
        code={state.code}
        onChange={handleChange}
        onSubmit={signInTwoFactor}
        useSms={useSms}
        sendingSMS={state.sendingSMS}
        error={state.error}
        disableSubmit={state.disable}
      />
    );
  }

  return (
    <Signin
      email={state.email}
      password={state.password}
      onChange={handleChange}
      onSubmit={signIn}
      errors={state.errors}
      disableSubmit={state.disable}
    />
  );
}

export default observer(SigninPage);
